import { useState, useEffect } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { ChevronLeft, MapPin } from 'lucide-react'
import toast from 'react-hot-toast'
import { TransmissionView, DEFAULT_THEME } from '@shared/TransmissionView'
import { getPostDetail, likeExhibitionPost, getCoordinateList } from '../api/transmissionService'
import { coordinateIdToTableId, findZoneByCoordinateId } from '../mapZones'
import { ROUTES } from '@app/router/routes'
import PageHeader from '@shared/layouts/PageHeader'
import BottomNav from '@shared/layouts/BottomNav'
import { getUserInfo } from '@shared/api/client'
import { useLanguage } from '@shared/contexts/LanguageContext'

const DEFAULT_SETTINGS = {
  intro: { gradient: true },
  modelImagePosition: 'left',
  modelTitleStyle: 'left',
  modelBg: 'color',
  modelSubtitleAlign: 'left',
  artifactImagePosition: 'right',
  artifactTitleStyle: 'left',
  artifactBg: 'color',
  silhouetteVariant: 1,
}

export default function TransmissionDetailPage() {
  const { monasteryId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  const { t, currentLanguage } = useLanguage()
  const monasteryName = location.state?.monasteryName ?? ''
  const mapRestore = location.state?.mapRestore ?? null
  const [user, setUser] = useState(() => getUserInfo())

  const [blocks, setBlocks] = useState([])
  const [theme, setTheme] = useState(DEFAULT_THEME)
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)
  const [isLoading, setIsLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768)
  const [postId, setPostId] = useState(null)
  const [likeCount, setLikeCount] = useState(0)
  const [isLiked, setIsLiked] = useState(false)
  const [likeLoading, setLikeLoading] = useState(false)
  const [locatingMap, setLocatingMap] = useState(false)

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])

  useEffect(() => {
    const onAuthChange = () => setUser(getUserInfo())
    window.addEventListener('auth-change', onAuthChange)
    return () => window.removeEventListener('auth-change', onAuthChange)
  }, [])

  useEffect(() => {
    if (!monasteryId) return
    setIsLoading(true)
    getPostDetail(monasteryId)
      .then(res => {
        const item = res.items?.[0]
        if (!item) { setNotFound(true); return }
        const raw = item.template_detail ?? item.detail ?? {}
        const detail = typeof raw === 'string' ? JSON.parse(raw) : raw
        setBlocks(detail.blocks ?? [])
        if (detail.theme) setTheme(detail.theme)
        if (detail.settings) setSettings(detail.settings)
        setPostId(item.post_id ?? null)
        setLikeCount(item.like_count ?? 0)
        setIsLiked(item.has_liked ?? item.is_liked ?? false)
      })
      .catch(() => setNotFound(true))
      .finally(() => setIsLoading(false))
  }, [monasteryId, currentLanguage])

  const handleLike = async () => {
    if (likeLoading || isLiked || !postId) return
    setIsLiked(true)
    setLikeCount(c => c + 1)
    setLikeLoading(true)
    try {
      const res = await likeExhibitionPost(postId)
      setLikeCount(res.like_count ?? likeCount + 1)
    } catch (err) {
      setIsLiked(false)
      setLikeCount(c => c - 1)
      toast.error(err?.message || err?.data?.reason || t('transmission-detail.likeFailed'))
    } finally {
      setLikeLoading(false)
    }
  }

  const handleLocateOnMap = async () => {
    if (locatingMap || !monasteryId) return
    setLocatingMap(true)
    try {
      const res = await getCoordinateList()
      const match = res.items?.find(item => String(item.monastery_id) === String(monasteryId))
      if (!match) {
        toast.error(t('transmission-detail.locateNotFound'))
        return
      }
      const activeZone = findZoneByCoordinateId(match.coordinate_id)
      navigate(ROUTES.TRANSMISSION, {
        state: {
          mapRestore: {
            isFullscreen: true,
            activeZoneId: 'zone2',
            zone2: { activeZone, selectedTableId: coordinateIdToTableId(match.coordinate_id) },
          },
        },
      })
    } catch (err) {
      toast.error(err?.message || err?.data?.reason || t('transmission-detail.locateFailed'))
    } finally {
      setLocatingMap(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <span className="material-symbols-outlined animate-spin text-4xl text-[var(--color-primary)]">
          progress_activity
        </span>
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-white relative">
        <PageHeader user={user} showHome />
        <div className="flex flex-col items-center justify-center gap-4 pt-32">
          <p className="text-lg" style={{ color: 'var(--color-text-sub)' }}>{t('transmission-detail.notFound')}</p>
          <button
            onClick={() => navigate(ROUTES.TRANSMISSION)}
            className="px-6 py-2 rounded-full text-sm font-medium"
            style={{ backgroundColor: 'var(--color-accent)', color: 'white' }}
          >
            {t('transmission-detail.backToList')}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="transmission-detail-page min-h-screen bg-white relative pb-20 md:pb-0 animate-zoomIn">
      <PageHeader user={user} showHome />

      {/* Back Button (fixed, stays on screen while scrolling) */}
      <div className="fixed top-[80px] md:top-[96px] left-4 md:left-8 z-30">
        <button
          onClick={() => navigate(ROUTES.TRANSMISSION, { state: { mapRestore }, replace: true })}
          className="flex justify-center items-center w-10 h-10 rounded-full bg-white/60 shadow-[0_2px_8px_rgba(0,0,0,0.1)] backdrop-blur text-[var(--color-primary)] hover:bg-white transition-colors cursor-pointer"
          aria-label={t('transmission-detail.backAria')}
        >
          <ChevronLeft size={24} className="mr-0.5" />
        </button>
      </div>

      {/* Locate on Map Button (fixed, stays on screen while scrolling) */}
      (<div className="fixed top-[80px] md:top-[96px] right-4 md:right-8 z-30">
        <button
          onClick={handleLocateOnMap}
          disabled={locatingMap}
          className="flex justify-center items-center w-10 h-10 rounded-full bg-white/60 shadow-[0_2px_8px_rgba(0,0,0,0.1)] backdrop-blur text-[var(--color-primary)] hover:bg-white transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          aria-label={t('transmission-detail.locateAria')}
        >
          <MapPin size={20} />
        </button>
      </div>)

      {/* Fixed Like Button */}
      {postId && (
        <div className="fixed right-4 md:right-6 bottom-24 z-50">
          <button
            onClick={handleLike}
            disabled={likeLoading || isLiked}
            className={`flex flex-col items-center gap-1 w-14 h-14 rounded-full border-2 shadow-xl transition-all duration-200 select-none
              ${isLiked
                ? 'bg-[var(--color-primary)] border-[var(--color-primary)] text-white cursor-default'
                : 'bg-white/90 border-[var(--color-primary)] text-[var(--color-primary)] hover:bg-[var(--color-primary)] hover:text-white cursor-pointer'
              }
              ${likeLoading ? 'opacity-60 cursor-not-allowed' : ''}
            `}
            aria-label={t('transmission-detail.likeAria')}
          >
            <span
              className="material-symbols-outlined text-xl leading-none mt-2"
              style={{ fontVariationSettings: isLiked ? "'FILL' 1" : "'FILL' 0" }}
            >
              favorite
            </span>
            <span className="text-xs font-bold leading-none mb-1">{likeCount}</span>
          </button>
        </div>
      )}

      <div className="pt-[70px]">
        <TransmissionView
          blocks={blocks}
          theme={theme}
          settings={settings}
          isMobile={isMobile}
        />
      </div>

      <BottomNav className="md:hidden" />
    </div>
  )
}
